// The vendor dialog: Details, Aliases, Rules and Email Capture tabs, and the
// Test box that shows which vendor and budget line a descriptor resolves to.

// REACT IMPORTS
import React, { useEffect, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import {
	Alert, Button, Checkbox, CircularProgress, Collapse, Dialog, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Table,
	TableBody, TableCell, TableHead, TableRow, TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ScienceIcon from "@mui/icons-material/Science";

// API, CONSTANTS
import { deleteAPICall, getAPICall, patchAPICall, postAPICall } from "../../../config/apiCalls";
import { apiError } from "../itBudgetUtils";

const BLANK_VENDOR = {
	name: "", it_vendor_category_id: "", gl_account_id: "", it_payment_method_id: "", it_card_status_id: "", renewal_date: "", notes: "",
	active: 1, email_sender: "", email_subject_filter: "", attachment_rule: "", file_name_token: "", capture_notes: "", move_processed_mail: 1,
	ai_prompt_key: "it_budget.receipt", prefill_from_history: 1,
};
const BLANK_ALIAS = { alias: "", match_source: "card_descriptor", match_type: "prefix", priority: 100 };
const BLANK_RULE = { priority: 10, field: "business_purpose", operator: "contains", value: "", target_it_budget_item_id: "", target_office_id: "", active: true };
const TABS = ["Details", "Aliases", "Rules", "Email Capture"];

const VendorDialog = ({ open, vendorId, canEdit, lookups, onClose, onSaved }) => {
	const { instance, accounts } = useMsal();
	const [tab, setTab] = useState("Details");
	const [vendor, setVendor] = useState(null);
	const [form, setForm] = useState(BLANK_VENDOR);
	const [alias, setAlias] = useState(BLANK_ALIAS);
	const [rule, setRule] = useState(BLANK_RULE);
	const [test, setTest] = useState({ descriptor: "", purpose: "", location: "" });
	const [testResult, setTestResult] = useState(null);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	const fromVendor = (v) => {
		const f = { ...BLANK_VENDOR };
		Object.keys(f).forEach((k) => (f[k] = v[k] ?? f[k]));
		f.renewal_date = v.renewal_date ? String(v.renewal_date).slice(0, 10) : "";
		return f;
	};

	useEffect(() => {
		if (!open) return;
		setTab("Details");
		setError("");
		setTestResult(null);
		setAlias(BLANK_ALIAS);
		setRule(BLANK_RULE);
		if (!vendorId) {
			setVendor(null);
			setForm(BLANK_VENDOR);
			return;
		}
		getAPICall(instance, accounts[0], `/api/it-budget/vendors/${vendorId}`)
			.then((res) => {
				setVendor(res.data);
				setForm(fromVendor(res.data));
			})
			.catch((err) => setError(apiError(err, "Couldn't load the vendor.")));
	}, [open, vendorId]);

	const set = (key) => (e) => setForm({ ...form, [key]: e.target.type === "checkbox" ? (e.target.checked ? 1 : 0) : e.target.value });

	const run = async (fn) => {
		setSaving(true);
		setError("");
		try {
			await fn();
		} catch (err) {
			setError(apiError(err, "Couldn't save."));
		} finally {
			setSaving(false);
		}
	};

	const saveVendor = () =>
		run(async () => {
			if (vendorId) {
				const res = await patchAPICall(instance, accounts[0], `/api/it-budget/vendors/${vendorId}`, { ...form, expectedModified: vendor.modified });
				setVendor(res.data.vendor);
				setForm(fromVendor(res.data.vendor));
				onSaved();
			} else {
				const res = await postAPICall(instance, accounts[0], "/api/it-budget/vendors", form);
				onSaved(res.data.vendor.id);
			}
		});

	const addAlias = () =>
		run(async () => {
			const res = await postAPICall(instance, accounts[0], `/api/it-budget/vendors/${vendorId}/aliases`, alias);
			setVendor(res.data.vendor);
			setAlias(BLANK_ALIAS);
			onSaved();
		});

	const removeAlias = (id) =>
		run(async () => {
			const res = await deleteAPICall(instance, accounts[0], `/api/it-budget/vendors/${vendorId}/aliases/${id}`);
			setVendor(res.data.vendor);
			onSaved();
		});

	const addRule = () =>
		run(async () => {
			const res = await postAPICall(instance, accounts[0], `/api/it-budget/vendors/${vendorId}/rules`, rule);
			setVendor(res.data.vendor);
			setRule(BLANK_RULE);
			onSaved();
		});

	const toggleRule = (r) =>
		run(async () => {
			const res = await patchAPICall(instance, accounts[0], `/api/it-budget/vendors/${vendorId}/rules/${r.id}`, { ...r, active: !r.active });
			setVendor(res.data.vendor);
		});

	const runTest = async () => {
		try {
			setTestResult((await postAPICall(instance, accounts[0], "/api/it-budget/vendors/test", test)).data);
		} catch (err) {
			setError(apiError(err, "Test failed."));
		}
	};

	const select = (label, key, options, { valueKey = "id", text = (o) => o.display_name, allowEmpty = true, required = false } = {}) => (
		<TextField select size="small" label={label} required={required} value={form[key] ?? ""} onChange={set(key)} disabled={!canEdit}>
			{allowEmpty && <MenuItem value="">—</MenuItem>}
			{(options || []).map((o) => (
				<MenuItem key={o[valueKey]} value={o[valueKey]}>
					{text(o)}
				</MenuItem>
			))}
		</TextField>
	);
	const text = (label, key, props = {}) => (
		<TextField size="small" label={label} value={form[key] ?? ""} onChange={set(key)} disabled={!canEdit} {...props} />
	);
	const tabsShown = vendorId ? TABS : ["Details", "Email Capture"];

	return (
		<Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
			<DialogTitle className="itBudgetDialogTitle">{vendorId ? vendor?.name || "Vendor" : "New Vendor"}</DialogTitle>
			<DialogContent className="itBudgetDialogContent">
				<Collapse in={Boolean(error)}>
					<Alert severity="error" onClose={() => setError("")}>
						{error}
					</Alert>
				</Collapse>
				<div className="itBudgetTabs">
					{tabsShown.map((t) => (
						<button key={t} className={`itBudgetTab${tab === t ? " itBudgetTab--active" : ""}`} onClick={() => setTab(t)}>
							{t}
							{t === "Aliases" && vendor ? ` (${vendor.aliases.length})` : ""}
							{t === "Rules" && vendor ? ` (${vendor.rules.length})` : ""}
						</button>
					))}
				</div>

				{tab === "Details" && (
					<div className="itBudgetFormGrid">
						{text("Vendor", "name", { required: true })}
						{select("Category", "it_vendor_category_id", lookups.categories, { allowEmpty: false, required: true })}
						{select("GL account", "gl_account_id", lookups.glAccounts, { text: (o) => `${o.gl_code} ${o.display_name}` })}
						{select("Paid By", "it_payment_method_id", lookups.paymentMethods, { allowEmpty: false, required: true })}
						{select("Card Update", "it_card_status_id", lookups.cardStatuses)}
						{text("Renewal", "renewal_date", { type: "date", InputLabelProps: { shrink: true } })}
						<FormControlLabel control={<Checkbox checked={Boolean(form.active)} onChange={set("active")} disabled={!canEdit} />} label="Active" />
						<FormControlLabel
							control={<Checkbox checked={Boolean(form.prefill_from_history)} onChange={set("prefill_from_history")} disabled={!canEdit} />}
							label="Prefill purpose from history"
						/>
						<div style={{ gridColumn: "1 / -1" }}>{text("Notes", "notes", { multiline: true, minRows: 2, fullWidth: true })}</div>
						<div className="itBudgetMuted" style={{ gridColumn: "1 / -1" }}>
							Default budget line and rule targets are set once budget items exist (Phase 3).
						</div>
					</div>
				)}

				{tab === "Email Capture" && (
					<div className="itBudgetFormGrid">
						{text("Email sender", "email_sender", { placeholder: "invoice+statements@mail.anthropic.com" })}
						{text("Subject filter", "email_subject_filter", { helperText: "Subject contains; separate alternatives with |" })}
						{text("Attachment rule", "attachment_rule", { helperText: "body, or file patterns like Receipt-*.pdf; empty = every PDF" })}
						{text("File token", "file_name_token", { helperText: "{date}_{token}_${total}.pdf" })}
						{select("Extraction prompt", "ai_prompt_key", (lookups.promptNames || []).map((n) => ({ id: n, display_name: n })), { allowEmpty: false })}
						<FormControlLabel
							control={<Checkbox checked={Boolean(form.move_processed_mail)} onChange={set("move_processed_mail")} disabled={!canEdit} />}
							label="Move processed mail to the processed folder"
						/>
						<div style={{ gridColumn: "1 / -1" }}>
							{text("Capture notes (sent to Claude after the prompt)", "capture_notes", { multiline: true, minRows: 4, fullWidth: true })}
						</div>
						<div className="itBudgetMuted" style={{ gridColumn: "1 / -1" }}>
							Every active mailbox is searched for this sender. More senders can be added as aliases with source email_sender.
						</div>
					</div>
				)}

				{tab === "Aliases" && vendor && (
					<>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Alias</TableCell>
									<TableCell>Source</TableCell>
									<TableCell>Match</TableCell>
									<TableCell align="right">Priority</TableCell>
									<TableCell />
								</TableRow>
							</TableHead>
							<TableBody>
								{vendor.aliases.map((a) => (
									<TableRow key={a.id}>
										<TableCell className="itBudgetMono">{a.alias}</TableCell>
										<TableCell>{a.match_source}</TableCell>
										<TableCell>{a.match_type}</TableCell>
										<TableCell align="right">{a.priority}</TableCell>
										<TableCell align="right">
											{canEdit && (
												<IconButton size="small" aria-label="Remove alias" onClick={() => removeAlias(a.id)}>
													<DeleteIcon fontSize="small" />
												</IconButton>
											)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
						{canEdit && (
							<div className="itBudgetFormGrid">
								<TextField size="small" label="Alias" value={alias.alias} onChange={(e) => setAlias({ ...alias, alias: e.target.value })} />
								<TextField select size="small" label="Source" value={alias.match_source} onChange={(e) => setAlias({ ...alias, match_source: e.target.value })}>
									{(lookups.matchSources || []).map((s) => (
										<MenuItem key={s} value={s}>
											{s}
										</MenuItem>
									))}
								</TextField>
								<TextField select size="small" label="Match" value={alias.match_type} onChange={(e) => setAlias({ ...alias, match_type: e.target.value })}>
									{(lookups.matchTypes || []).map((s) => (
										<MenuItem key={s} value={s}>
											{s}
										</MenuItem>
									))}
								</TextField>
								<TextField size="small" label="Priority" value={alias.priority} onChange={(e) => setAlias({ ...alias, priority: e.target.value })} />
								<Button variant="outlined" onClick={addAlias} disabled={saving || !alias.alias.trim()}>
									Add Alias
								</Button>
							</div>
						)}
					</>
				)}

				{tab === "Rules" && vendor && (
					<>
						<div className="itBudgetOverline">Classification rules · first match wins</div>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell align="right">Priority</TableCell>
									<TableCell>When</TableCell>
									<TableCell>Budget Line</TableCell>
									<TableCell>OIC</TableCell>
									<TableCell>Active</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{vendor.rules.map((r) => (
									<TableRow key={r.id}>
										<TableCell align="right">{r.priority}</TableCell>
										<TableCell>
											{r.field} <b>{r.operator}</b> <span className="itBudgetMono">{r.value}</span>
										</TableCell>
										<TableCell>{r.target_budget_line || "—"}</TableCell>
										<TableCell>{r.target_office || "—"}</TableCell>
										<TableCell>
											<Checkbox size="small" checked={Boolean(r.active)} disabled={!canEdit || saving} onChange={() => toggleRule(r)} />
										</TableCell>
									</TableRow>
								))}
								<TableRow>
									<TableCell align="right" className="itBudgetMuted">
										default
									</TableCell>
									<TableCell className="itBudgetMuted">No rule matched</TableCell>
									<TableCell className="itBudgetMuted">{vendor.default_budget_line || "Needs Review"}</TableCell>
									<TableCell className="itBudgetMuted">—</TableCell>
									<TableCell className="itBudgetMuted">Vendor default</TableCell>
								</TableRow>
							</TableBody>
						</Table>
						{canEdit && (
							<div className="itBudgetFormGrid">
								<TextField size="small" label="Priority" value={rule.priority} onChange={(e) => setRule({ ...rule, priority: e.target.value })} />
								<TextField select size="small" label="When" value={rule.field} onChange={(e) => setRule({ ...rule, field: e.target.value })}>
									{(lookups.ruleFields || []).map((s) => (
										<MenuItem key={s} value={s}>
											{s}
										</MenuItem>
									))}
								</TextField>
								<TextField select size="small" label="Operator" value={rule.operator} onChange={(e) => setRule({ ...rule, operator: e.target.value })}>
									{(lookups.ruleOperators || []).map((s) => (
										<MenuItem key={s} value={s}>
											{s}
										</MenuItem>
									))}
								</TextField>
								<TextField size="small" label="Value" value={rule.value} onChange={(e) => setRule({ ...rule, value: e.target.value })} />
								<TextField select size="small" label="Budget line" value={rule.target_it_budget_item_id} onChange={(e) => setRule({ ...rule, target_it_budget_item_id: e.target.value })}>
									<MenuItem value="">— (Phase 3)</MenuItem>
									{(lookups.budgetItems || []).map((b) => (
										<MenuItem key={b.id} value={b.id}>
											{b.purpose}
										</MenuItem>
									))}
								</TextField>
								<TextField select size="small" label="OIC override" value={rule.target_office_id} onChange={(e) => setRule({ ...rule, target_office_id: e.target.value })}>
									<MenuItem value="">—</MenuItem>
									{(lookups.offices || []).map((o) => (
										<MenuItem key={o.id} value={o.id}>
											{o.state}
										</MenuItem>
									))}
								</TextField>
								<Button variant="outlined" onClick={addRule} disabled={saving || !String(rule.value).trim()}>
									Add Rule
								</Button>
							</div>
						)}
					</>
				)}

				<div className="itBudgetTestBox">
					<div className="itBudgetOverline">Test</div>
					<div className="itBudgetFormGrid">
						<TextField size="small" label="Card Descriptor" value={test.descriptor} onChange={(e) => setTest({ ...test, descriptor: e.target.value })} />
						<TextField size="small" label="Business Purpose" value={test.purpose} onChange={(e) => setTest({ ...test, purpose: e.target.value })} />
						<TextField select size="small" label="Location" value={test.location} onChange={(e) => setTest({ ...test, location: e.target.value })}>
							<MenuItem value="">All Offices</MenuItem>
							{(lookups.offices || []).map((o) => (
								<MenuItem key={o.id} value={o.state}>
									{o.state}
								</MenuItem>
							))}
						</TextField>
						<Button variant="outlined" startIcon={<ScienceIcon />} onClick={runTest} disabled={!test.descriptor.trim()}>
							Test
						</Button>
					</div>
					{testResult && <div style={{ fontSize: 13 }}>{testResult.message}</div>}
				</div>

				<div className="itBudgetDialogButtons">
					<Button variant="outlined" color="error" onClick={onClose} disabled={saving}>
						{canEdit ? "Cancel" : "Close"}
					</Button>
					{canEdit && (tab === "Details" || tab === "Email Capture") && (
						<Button variant="contained" onClick={saveVendor} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>
							Save Vendor
						</Button>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default VendorDialog;
